import {
  Checklist, ChecklistRecipient,
  ChecklistState,
  ChecklistSummary, EmailContact,
} from "../api/dtos/dtos";
import {ColumnFilterItem} from "antd/lib/table";
import locales from "../locales";
import {AppColors} from "../statistics/utils/colors";
import {PropertyType} from "./send/ChecklistTypes";


export interface ChecklistStateDto {
  color: string;
  text: string;
}

type ObjectWithValuesOfEnumAsKeys = { [key in ChecklistState]: ChecklistStateDto };

export const checklistStateToDto: ObjectWithValuesOfEnumAsKeys = {
  [ChecklistState.NOT_REQUIRED]: {text: locales.checklist_state_not_required, color: AppColors.TRANSPARENT}, // Never displayed
  [ChecklistState.MISSING]: {text: locales.checklist_state_missing, color: AppColors.RED},
  [ChecklistState.MISSING_ATTACHMENTS]: {text: locales.checklist_state_missing_attachments, color: AppColors.RED},
  [ChecklistState.READY]: {text: locales.checklist_state_ready, color: AppColors.YELLOW},
  [ChecklistState.SENT_OUT]: {text: locales.checklist_state_sent_out, color: AppColors.MEDIUM_GRAY},
  [ChecklistState.ACCEPTED]: {text: locales.checklist_state_accepted, color: ''},
  [ChecklistState.IGNORED]: {text: locales.checklist_state_ignored, color: AppColors.MEDIUM_GRAY}
};

export function getChecklistAlertStateFilters(): ColumnFilterItem[] {
  return [ChecklistState.MISSING, ChecklistState.MISSING_ATTACHMENTS, ChecklistState.READY, ChecklistState.SENT_OUT, ChecklistState.ACCEPTED, ChecklistState.IGNORED]
    .map(state => ({text: checklistStateToDto[state].text, value: state}));
}


export const findSendoutAtDate = (summary: ChecklistSummary | undefined): string | null | undefined => {
  return summary?.checklists.find(c => c.sendoutAt !== null)?.sendoutAt;
}

export const findAcceptedAtDate = (summary: ChecklistSummary | undefined): string | null | undefined => {
  return summary?.checklists.find(c => c.sendoutAt !== null)?.acceptedAt;
}

export const findAcceptedBy = (summary: ChecklistSummary | undefined): EmailContact | null | undefined => {
  return summary?.checklists.find(c => c.acceptedBy !== null)?.acceptedBy;
}

export const findSendoutRecipients = (summary: ChecklistSummary | undefined): ChecklistRecipient[] | undefined => {
  return summary?.checklists.find((c) => c.recipients.length > 0)?.recipients;
}

export const findWithAllRequiredAttachments = (checklists: Checklist[] | undefined): Checklist[] | undefined => {
  return checklists?.filter((c) => c.attachments.length >= 5);
}

export const findAllNotIgnored = (summary: ChecklistSummary | undefined): Checklist[] | undefined => {
  return summary?.checklists.filter((c) => c.state !== ChecklistState.IGNORED);
}

export const findAllIgnored = (summary: ChecklistSummary | undefined): Checklist[] | undefined => {
  return summary?.checklists.filter((c) => c.state === ChecklistState.IGNORED);
}

export const isAccepted = (summary: ChecklistSummary | undefined): boolean => {
  const acceptedAt = findAcceptedAtDate(summary);
  return acceptedAt !== undefined && acceptedAt !== null;
}

export const hasChecklistCountMatch = (summary: ChecklistSummary | undefined): boolean => {
  const notIngoredOnes = findAllNotIgnored(summary);

  return summary !== undefined &&
    summary.requiredCount === summary.checklists.length &&
    notIngoredOnes?.length === findWithAllRequiredAttachments(notIngoredOnes)?.length;
}

export const findChecklistByRentalItemId = (checklists: Checklist[] | undefined, rentalItemId: number): Checklist | undefined => {
  return checklists?.find((c) => c.rentalItem.id! === rentalItemId);
}

export enum ChecklistFormMode {
  CREATE_RETURNAL = 'CREATE_RETURNAL',
  CREATE_RENTAL = 'CREATE_RENTAL',
  EDIT_RETURNAL = 'EDIT_RETURNAL',
  EDIT_RENTAL = 'EDIT_RENTAL',
  VIEW_RETURNAL = 'VIEW_RETURNAL',
  VIEW_RENTAL = 'VIEW_RENTAL',
}

export const isRentalMode = (mode: ChecklistFormMode): boolean => {
  return mode === ChecklistFormMode.CREATE_RENTAL || mode === ChecklistFormMode.EDIT_RENTAL || mode === ChecklistFormMode.VIEW_RENTAL;
}

export const isReturnalMode = (mode: ChecklistFormMode): boolean => {
  return mode === ChecklistFormMode.CREATE_RETURNAL || mode === ChecklistFormMode.EDIT_RETURNAL || mode === ChecklistFormMode.VIEW_RETURNAL;
}


export interface ChecklistFormProperty {
  label: string,
  key: string,
  type: PropertyType,
  rental?: PropertyValues,
  rentalEditable: boolean,
  returnal?: PropertyValues,
  returnalEditable: boolean,
}

export interface PropertyValues {
  value?: string,
  comment?: string,
}


// WEBPACK FOOTER //
// ./src/checklist/dtos.ts